import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";

export const Faq = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="border-t border-white/10 bg-[#0d0d0d] py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel text={t.faq.label} testId="faq-label" />
              <h2
                data-testid="faq-heading"
                className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-5xl"
              >
                {t.faq.heading}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            {t.faq.items.map((item, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div
                    data-testid={`faq-item-${i}`}
                    className="border-t border-white/10 last:border-b"
                  >
                    <button
                      data-testid={`faq-question-${i}`}
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="group flex w-full items-center justify-between gap-6 py-6 text-left md:py-7"
                    >
                      <span className="font-display text-lg font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-gold md:text-xl">
                        {item.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                          isOpen
                            ? "rotate-45 border-gold text-gold"
                            : "border-white/15 text-neutral-400 group-hover:border-gold/50 group-hover:text-gold"
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p
                            data-testid={`faq-answer-${i}`}
                            className="max-w-2xl pb-7 text-sm leading-relaxed text-neutral-400 md:text-base"
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
